<template>
  <!-- ------------------------------------------------------------>
  <!-- MAIN CONTAINER -->
  <!-- -------------- -->
  <div class="row mt-3 mb-3">
    <!-- ------------------ -->
    <!--   delete confirm   -->
    <!-- ------------------ -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <div class="col-xl-11">
      <div class="row">
        <p class="bb-sheet-title">COUNTS</p>
      </div>
      <div class="container-fluid mt-3 mb-2">
        <!-- ----------------------------------------------------- -->
        <!-- headings -->
        <!-- ----------------------------------------------------- -->
        <div class="row">
          <div class="col-4 text-center">
            <p class="bb-hdg mt-2 mb-3">Count Name</p>
          </div>
          <div class="bb-col-counter text-center">
            <p class="bb-hdg mt-2 mb-3">Count Value</p>
          </div>

          <div class="col-auto ms-3">
            <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
          </div>
          <div class="col-auto ms-3">
            <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- data rows -->
        <!-- ----------------------------------------------------- -->
        <template
          v-for="(a, idx) in local_counts"
          :key="a.id || idx"
        >
          <div class="row mt-3 mb-3 align-items-center">
            <!-- ----------------------------------------------------- -->
            <!-- count name -->
            <!-- ----------------------------------------------------- -->
            <div class="col-4">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    :readonly="a.starter"
                    class="form-control capitalize"
                    :value="a.name"
                    v-bind:class="{
                      'is-invalid': getErrorMessage(idx, 'name'),
                    }"
                    @keydown="onKeyDownCountName($event)"
                    @input="onNameInput($event, idx)"
                    autocomplete="off"
                    :id="`count-name-${a.id || 'new'}-${idx}`"
                  />
                  <label
                    class="readonly bb-float-label optional"
                    :for="`count-name-${a.id || 'new'}-${idx}`"
                    >Name
                  </label>
                </div>
              </div>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- count value -->
            <!-- ----------------------------------------------------- -->
            <div class="bb-col-counter">
              <div class="form-group">
                <div
                  class="form-floating"
                  v-if="!a.name"
                >
                  <input
                    readonly
                    class="form-control text-center"
                    :value="a.value"
                    :id="`count-value-${a.id || 'new'}-${idx}`"
                    disabled
                  />
                  <label
                    class="readonly bb-float-label optional"
                    :for="`count-value-${a.id || 'new'}-${idx}`"
                    >Value
                  </label>
                </div>
                <div v-else>
                  <CounterEmit
                    :pass_ref="`counter-count-value${idx}`"
                    :pass_label="`Value`"
                    :pass_count="a.value"
                    :pass_min="0"
                    :pass_max="99"
                    :pass_digits="2"
                    :pass_skip_zero="false"
                    v-bind:class="{
                      'is-invalid': getErrorMessage(idx, 'value'),
                    }"
                    @change="onCounterChanged"
                  />
                </div>
              </div>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- delete button -->
            <!-- ----------------------------------------------------- -->
            <div class="col-auto d-flex align-items-center gap-2">
              <button
                :disabled="a.starter"
                :id="`count-delete-${a.id || 'new'}-${idx}`"
                type="button"
                @click="confirmDeletion(idx)"
                class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
              >
                <ToolTip
                  text="Delete Count"
                  position="left"
                >
                  <img
                    :src="deleteImg"
                    height="48"
                    width="48"
                    alt="delete image"
                  />
                </ToolTip>
              </button>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- id (hidden from view) -->
            <!-- ----------------------------------------------------- -->
            <div class="col-4 d-none">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-left"
                    :value="a.id"
                    :id="`count-id-${a.id || 'new'}-${idx}`"
                  />
                  <label
                    class="readonly bb-float-label optional"
                    :for="`count-id-${a.id || 'new'}-${idx}`"
                    >Id
                  </label>
                </div>
              </div>
            </div>
          </div>
          <!-- ----------------------------------------------------- -->
          <!-- row-level errors -->
          <!-- ----------------------------------------------------- -->
          <div
            v-if="hasRowError(idx)"
            class="row mt-1 mb-3"
          >
            <div class="col-3"></div>
            <div class="col-9">
              <div class="bb-error">
                <p class="mb-0">
                  <span
                    v-for="(msg, i) in getRowErrors(idx)"
                    :key="i"
                    >{{ msg }}</span
                  >
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'

import ConfirmDialog from '../components/ConfirmDialog.vue'
import ToolTip from '../components/ToolTip.vue'
import CounterEmit from './CounterEmit.vue'

import isAlphanumeric from '../helpers/isAlphanumeric.js'
import isEmpty from '../helpers/isEmpty.js'

import deleteImg from '../assets/images/bbDelete48.png'

export default {
  name: 'CharacterSheetCounts',
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    CounterEmit,
    ToolTip,
    ConfirmDialog,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // data
  //------
  data() {
    return {
      deleteImg,
      //
      delete: false,
      //
      local_character: null,
      addCountRowListener: null,
      //
      local_counts: [],
      countValueListeners: [],
      errors: [],
      // for undo detection
      baselineCounts: [],
      // for change detection
      originalCounts: [],
      // debounced emits per field key
      pendingTimers: {},
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    character: {
      handler(newCharacter) {
        //-------------------------------------------------
        // copy received counts & cap first letter of name
        //-------------------------------------------------
        this.local_counts = newCharacter.counts.map((a) => ({
          ...a,
          name: a.name || '',
        }))
        //--------------------------------
        // save values for undo detection
        //--------------------------------
        this.setBaselineCounts()
        //-----------------------------------------
        // add empty row where initially no counts
        //-----------------------------------------
        if (newCharacter.counts.length === 0) {
          this.addCountRow()
        }
      },
      immediate: true,
      deep: true,
    },
  },
  //========================================================
  // computed
  //-----------
  computed: {
    hasInvalidCount() {
      return this.local_counts.some((a) => isEmpty(a.name))
    },
    canAdd() {
      return !this.hasInvalidCount
    },
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    // changedFlags() {
    //   return this.local_counts.map((a, idx) => {
    //     const orig = this.baselineCounts[idx];
    //     if (!orig || !a) return false;
    //     return (
    //       a.name !== orig.name ||
    //       a.value !== orig.value
    //     );
    //   });
    // },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //=============================================
    // vuex functions
    //----------------
    // ...mapMutations('auth', {
    //   setCurrentView: 'setCurrentView',
    // }),
    //=============================================
    // has row error
    //---------------
    hasRowError(idx) {
      return this.errors && this.errors.some((e) => e.id === idx)
    },
    //=============================================
    // get error message
    //-------------------
    getErrorMessage(idx, field) {
      if (!this.errors || !Array.isArray(this.errors)) return ''
      const e = this.errors.find((er) => er.id === idx && er.field === field)
      return e ? e.message : ''
    },
    //=============================================
    // get row errors
    //----------------
    getRowErrors(idx) {
      if (!this.errors) return []
      // return readable messages
      return this.errors.filter((e) => e.id === idx).map((e) => e.message)
    },
    //=============================================
    // on name input
    //---------------
    onNameInput(event, idx) {
      const val = event.target.value
      const sanitised = val.replace(/[^A-Za-z ]/g, '').replace(/^\s+/, '')
      this.local_counts[idx].name = sanitised
      if (event.target.value !== this.local_counts[idx].name) {
        event.target.value = this.local_counts[idx].name
      }
      // clear any validation error for this row
      this.errors = this.errors.filter((e) => e.id !== idx)
      // schedule debounced emit for this name field
      this.scheduleDebouncedEmit(`name-${idx}`, {
        index: idx,
        field: 'name',
        value: this.local_counts[idx].name,
        id: this.local_counts[idx].id || null,
      })
    },
    //=============================================
    // set view
    //----------
    setView(view) {
      this.setCurrentView(view)
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //========================================================
    // on key down count name
    //------------------------
    onKeyDownCountName(e) {
      if (e.key === 'Delete' || e.key === 'Backspace' || e.key === 'Tab') {
        return
      }

      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) {
        e.preventDefault()
        return
      }

      if (e.key === ' ') {
        if (e.target.selectionStart === 0) {
          e.preventDefault()
        }
        return
      }

      if (!/^[A-Za-z]$/.test(e.key)) {
        e.preventDefault()
      }
    },
    //=============================================
    // add count row
    //---------------
    addCountRow() {
      // new rows start without a persisted id;
      // the parent decides when/how to create one.
      const newCount = {
        id: null,
        name: '',
        value: 0,
      }
      // push new count to local array
      this.local_counts.push(newCount)
      // create listener for new row
      this.createCountListener(this.local_counts.length - 1)
    },
    // set original counts for change detection
    //------------------------------------------
    setOriginalCounts() {
      // console.log('----------------------------------')
      // console.log('setOriginalCounts called')
      // console.log('local_counts at setOriginalCounts = ', this.local_counts)

      this.originalCounts = this.local_counts.map((a) => ({
        id: a.id,
        name: a.name,
        value: a.value,
      }))
      // console.log('Original counts set to:', this.originalCounts);
      // console.log('originalCounts length:', this.originalCounts.length);
    },
    //=============================================
    // set baseline counts for undo detection
    //----------------------------------------
    setBaselineCounts() {
      // console.log('----------------------------------')
      // console.log('setBaselineCounts called');
      this.baselineCounts = this.local_counts.map((a) => ({
        id: a.id,
        name: a.name,
        value: a.value,
      }))
      // console.log('Baseline counts set to:', this.baselineCounts);
      // console.log('baselineCounts length:', this.baselineCounts.length);
    },
    //=============================================
    // create count listener
    //---------------------
    createCountListener(idx) {
      // define event name
      const eventName = `counter-count-value${idx}`
      //  unregister previous listener if any
      const prevListener = this.countValueListeners[idx]
      if (prevListener) {
        this.emitter.off(eventName, prevListener)
      }
      // create listener function
      const listener = (return_count) => {
        this.local_counts[idx].value = return_count
        // clear any validation error for this row when the value changes
        this.errors = this.errors.filter((e) => e.id !== idx)
      }
      // save listener
      this.countValueListeners[idx] = listener
      // register listener
      this.emitter.on(eventName, listener)
    },
    //=============================================
    // confirm deletion
    //------------------
    async confirmDeletion(idx) {
      this.delete = true
      const ok = await this.$refs.confirmDialog.show({
        title: 'Delete Count',
        message: 'Confirm Deletion',
        subject: this.local_counts[idx]
          ? this.local_counts[idx].name
          : 'Count ' + (idx + 1),
        okButton: 'OK',
        cancelButton: 'Cancel',
      })
      if (ok) {
        //-------------------
        // emit delete event
        //-------------------
        this.$emit('delete-character-count', this.local_counts[idx])
        //-----------------------
        // remove row from array
        //-----------------------
        this.local_counts.splice(idx, 1)
        //-------------------------------------
        // add empty row if all counts deleted
        //-------------------------------------
        if (this.local_counts.length === 0) {
          this.addCountRow()
        }
        //--------------------------------
        // reset baseline counts for undo
        //--------------------------------
        this.setBaselineCounts()
      } else {
        this.delete = false
      }
    },
    //=============================================
    // unregister all listeners
    //--------------------------
    unregisterAllListeners() {
      // loop saved listeners (not local_counts which might be empty)
      this.countValueListeners.forEach((handler, idx) => {
        if (handler) {
          const eventName = `counter-count-value${idx}`
          // console.log('Unregistering:', eventName);
          this.emitter.off(eventName, handler)
        }
      })
      // unregister static listeners
      if (this.addCountRow)
        this.emitter.off('add-count-row', this.addCountRowListener)
      //
      this.baselineCounts = []
      this.countValueListeners = []

      // console.log('All listeners unregistered');
    },
    //=============================================
    // validate counts
    //-----------------
    validateCounts(counts = this.local_counts) {
      let errors = []
      //------------------------
      // check for empty fields
      //------------------------
      counts.forEach((count, idx) => {
        if (!count.name && count.value) {
          errors.push({
            id: idx,
            field: 'name',
            message: window.bbUiErrorMsg('Name required'),
          })
        } else if (
          count.name &&
          !/^(?=.*[A-Za-z])[A-Za-z][A-Za-z ]*$/.test(count.name)
        ) {
          errors.push({
            id: idx,
            field: 'name',
            message: window.bbUiErrorMsg(
              'Name must use letters and spaces only',
            ),
          })
        }
      })
      //------------------------
      // check for unique names
      //------------------------
      const nameMap = {}
      counts.forEach((count, idx) => {
        if (count.name) {
          nameMap[count.name] = (nameMap[count.name] || []).concat(idx)
        }
      })
      Object.keys(nameMap).forEach((name) => {
        const idxs = nameMap[name]
        if (idxs.length > 1) {
          // push one error object per dup row index
          idxs.forEach((i) => {
            errors.push({
              id: i,
              field: 'name',
              message: window.bbUiErrorMsg('Names must be unique'),
            })
          })
        }
      })
      //---------------
      // return errors
      //---------------
      return errors
    },
    //========================================================
    // handle add count row
    //-----------------------
    handleAddCountRow() {
      console.log('IN handleAddCountRow')
      this.addCountRow()
    },
    //========================================================
    // on counter changed
    //--------------------
    async onCounterChanged({ ref, count }) {
      console.log('onCounterChanged ref = ', ref, ' count = ', count)
      //------------------------------------
      // extract row index from ref string
      //------------------------------------
      const match = /counter-count-value(\d+)/.exec(ref || '')
      if (!match) {
        return
      }
      const idx = Number.parseInt(match[1], 10)
      if (Number.isNaN(idx) || !this.local_counts[idx]) {
        return
      }
      //---------------------------------
      // update local state for that row
      //---------------------------------
      this.local_counts[idx].value = count
      // clear any validation error for this row
      this.errors = this.errors.filter((e) => e.id !== idx)
      // schedule debounced emit for this value field
      this.scheduleDebouncedEmit(`value-${idx}`, {
        index: idx,
        field: 'value',
        value: count,
        id: this.local_counts[idx].id || null,
      })
    },
    //========================================================
    // schedule debounced emit
    //-------------------------
    scheduleDebouncedEmit(key, payload, delay = 600) {
      if (this.pendingTimers[key]) {
        clearTimeout(this.pendingTimers[key])
      }
      console.log('Scheduling emit for key = ', key, ' payload = ', payload)
      this.pendingTimers[key] = setTimeout(() => {
        // run validation before emitting
        const errors = this.validateCounts()
        this.errors = errors

        if (errors.length === 0) {
          const idx = payload.index
          const row = this.local_counts[idx]
          if (!row) {
            delete this.pendingTimers[key]
            return
          }

          const field = payload.field
          const value = payload.value
          const id = row.id || null
          const name = row.name || ''

          // no id => treat as a new count row
          if (!id) {
            // parent only needs the name; it will
            // create the count (value can default 0)
            this.$emit('add-character-count', {
              name,
            })
          } else {
            // existing count => normal update routed via handleUpdate
            this.$emit('update-character-count', {
              type: 'counts',
              key: field,
              value,
              name,
              id,
            })
          }
        }
        delete this.pendingTimers[key]
      }, delay)
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //----------------------------------
    // save values for change detection
    //----------------------------------
    this.setOriginalCounts()
    //--------------------------------
    // save values for undo detection
    //--------------------------------
    this.setBaselineCounts()
    //--------------------------------
    // register dynamic count listeners
    //--------------------------------
    this.local_counts.forEach((_, idx) => {
      this.createCountListener(idx)
    })
    //----------------------------
    // add new count row listener
    //----------------------------
    this.addCountRowListener = () => {
      this.addCountRow()
    }
    this.emitter.on('add-count-row', this.addCountRowListener)
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    this.unregisterAllListeners()
    // clear any outstanding timers
    Object.values(this.pendingTimers || {}).forEach((t) => clearTimeout(t))
    this.pendingTimers = {}
  },
}
</script>
